import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { ApiClientConstant } from '@cureskin/api-client';
import { CsIconModule } from '@components/icon';
import { LoadingModule } from '@components/loading';
import { EventLoggerService } from '@services/event-logger-service';

export interface ChangeRegimenAnswerBottomsheetData {
  /** Regimen class (FACE / HAIR) — drives the dynamic Skin/Hair copy. */
  regimenClass: string;
  /** Invoked when the user confirms they want to start the consultation again. Resolves once the restart API + navigation have been triggered. */
  startAgainCallback: () => Promise<void>;
}

/**
 * Confirmation sheet shown before re-answering the consultation questions
 * (under the `restart_regimen_chat_UI` experiment). Opened from the
 * {@link ReanswerRegimenQuestionBannerComponent}.
 */
@Component({
  selector: 'app-change-regimen-answer-bottomsheet',
  templateUrl: './change-regimen-answer-bottomsheet.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CsIconModule, LoadingModule],
})
export class ChangeRegimenAnswerBottomsheetComponent implements OnInit {
  protected readonly data = inject<ChangeRegimenAnswerBottomsheetData>(MAT_BOTTOM_SHEET_DATA);
  private readonly bottomSheetRef = inject(MatBottomSheetRef<ChangeRegimenAnswerBottomsheetComponent>);
  private readonly eventLogger = inject(EventLoggerService);

  protected readonly isFaceRegimen = this.data.regimenClass === ApiClientConstant.Regimen.Class.FACE;

  /** True while the restart API call is in flight — locks the sheet and shows a spinner on the confirm button. */
  protected readonly isLoading = signal(false);

  ngOnInit(): void {
    this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ name: 'change-regimen-answer-bottomsheet' }));
  }

  protected keepAnswers(): void {
    if (this.isLoading()) return;
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'change-regimen-answer-keep-answers' }));
    this.bottomSheetRef.dismiss();
  }

  protected async startAgain(): Promise<void> {
    if (this.isLoading()) return;
    this.isLoading.set(true);
    // Lock the sheet (no backdrop/escape dismiss) while the restart call is in flight.
    this.bottomSheetRef.disableClose = true;
    try {
      await this.data.startAgainCallback();
      this.bottomSheetRef.dismiss();
    } catch {
      // Keep the sheet open so the user can retry if the restart call failed.
      this.isLoading.set(false);
      this.bottomSheetRef.disableClose = false;
    }
  }
}
